import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  LogOut, Loader2, PlayCircle, StopCircle, FileDown, Plus, Calendar as CalIcon,
  Clock, User as UserIcon, ListChecks,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sriman-enterprise/artifacts/ng31c679_image.png";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0,
}).format(n || 0);

const today = () => new Date().toISOString().slice(0, 10);
const nowMonth = () => `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,"0")}`;

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function EmployeeDashboard() {
  const { user, logout, apiBase, authHeader } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [leaveForm, setLeaveForm] = useState({
    type: "casual", from_date: today(), to_date: today(), reason: "",
  });
  const [onboarding, setOnboarding] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meR, atR, lvR, psR, obR] = await Promise.all([
        axios.get(`${apiBase}/employees/me`, { headers: authHeader() }),
        axios.get(`${apiBase}/attendance/me`, {
          params: { month: nowMonth() }, headers: authHeader(),
        }),
        axios.get(`${apiBase}/leaves/me`, { headers: authHeader() }),
        axios.get(`${apiBase}/payslips/me`, { headers: authHeader() }),
        axios.get(`${apiBase}/onboarding/me`, { headers: authHeader() }),
      ]);
      setMe(meR.data);
      setAttendance(atR.data || []);
      setLeaves(lvR.data || []);
      setPayslips(psR.data || []);
      setOnboarding(obR.data);
    } catch {
      toast.error("Failed to load your HRMS data");
    } finally { setLoading(false); }
  }, [apiBase, authHeader]);

  useEffect(() => { load(); }, [load]);

  const todayEntry = useMemo(
    () => attendance.find((a) => a.date === today()),
    [attendance],
  );

  const checkIn = async () => {
    setBusy("in");
    try {
      await axios.post(`${apiBase}/attendance/check-in`, {}, { headers: authHeader() });
      toast.success("Checked in");
      await load();
    } catch { toast.error("Check-in failed"); }
    finally { setBusy(""); }
  };

  const checkOut = async () => {
    setBusy("out");
    try {
      await axios.post(`${apiBase}/attendance/check-out`, {}, { headers: authHeader() });
      toast.success("Checked out");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-out failed");
    } finally { setBusy(""); }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    setBusy("leave");
    try {
      await axios.post(`${apiBase}/leaves`, leaveForm, { headers: authHeader() });
      toast.success("Leave request submitted");
      setLeaveForm({ type: "casual", from_date: today(), to_date: today(), reason: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit leave");
    } finally { setBusy(""); }
  };

  const downloadPayslip = async (ps) => {
    try {
      const resp = await axios.get(`${apiBase}/payslips/${ps.id}/download`, {
        headers: authHeader(), responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${ps.payslip_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const doLogout = async () => {
    await logout();
    navigate("/hrms/login", { replace: true });
  };

  return (
    <main data-testid="employee-dashboard-page" className="min-h-screen bg-[#040812] pt-24 pb-24">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#040812]/95 backdrop-blur-xl fixed top-0 inset-x-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="h-9 w-9">
              <img src={LOGO_URL} alt="Sriman Solutions" className="h-full w-full object-contain" />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-display text-white text-sm tracking-tight">Sriman Solutions</span>
              <span className="font-mono text-[9px] text-red-500 tracking-[0.28em] uppercase mt-1">
                HRMS
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:block text-xs text-slate-500 font-mono">
              {user?.name} · {user?.email}
            </span>
            <button onClick={doLogout} data-testid="hrms-logout"
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-xs font-medium px-4 py-2 rounded-sm">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-4">
        {/* Profile card */}
        <div className="bg-[#0B132B]/60 border border-white/10 p-6 md:p-8">
          <div className="flex items-center gap-2 overline">
            <UserIcon size={12} /> Employee profile
          </div>
          <h1 className="mt-3 font-display text-white text-3xl md:text-4xl tracking-tighter font-light">
            {me?.name || user?.name}
          </h1>
          <div className="mt-3 grid md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
            <div>
              <div className="overline mb-1">Designation</div>
              <div className="text-white">{me?.designation || "—"}</div>
            </div>
            <div>
              <div className="overline mb-1">Department</div>
              <div className="text-white">{me?.department || "—"}</div>
            </div>
            <div>
              <div className="overline mb-1">Employee code</div>
              <div className="text-white font-mono text-xs">{me?.employee_code || "—"}</div>
            </div>
            <div>
              <div className="overline mb-1">Joining date</div>
              <div className="text-white font-mono text-xs">{me?.joining_date || "—"}</div>
            </div>
          </div>
        </div>

        {/* Onboarding checklist */}
        {onboarding && (onboarding.items || []).length > 0 && (() => {
          const items = onboarding.items;
          const done = items.filter((i) => i.done).length;
          const toggleItem = async (key) => {
            try {
              const { data } = await axios.patch(
                `${apiBase}/onboarding/me/${key}`, {}, { headers: authHeader() },
              );
              setOnboarding((o) => ({ ...o, items: data.items }));
            } catch { toast.error("Update failed"); }
          };
          return (
            <div className="mt-6 bg-[#0B132B]/50 border border-white/10 p-6" data-testid="hrms-onboarding">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 overline">
                  <ListChecks size={12} /> Day-1 onboarding checklist
                </div>
                <div className="font-mono text-[10px] tracking-widest uppercase">
                  <span className={done === items.length ? "text-green-400" : "text-yellow-300"}>
                    {done}/{items.length} complete
                  </span>
                </div>
              </div>
              <div className="mt-4 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 transition-all" style={{ width: `${(done / items.length) * 100}%` }} />
              </div>
              <ul className="mt-4 grid md:grid-cols-2 gap-2">
                {items.map((it) => (
                  <li key={it.key}>
                    <button
                      onClick={() => toggleItem(it.key)}
                      data-testid={`hrms-onboarding-${it.key}`}
                      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border text-sm transition-colors ${
                        it.done
                          ? "border-green-500/40 bg-green-500/10 text-green-300"
                          : "border-white/10 text-slate-300 hover:border-white/30"
                      }`}
                    >
                      <span className={`w-4 h-4 shrink-0 border flex items-center justify-center ${
                        it.done ? "border-green-400 bg-green-400/20" : "border-slate-500"
                      }`}>
                        {it.done ? "✓" : ""}
                      </span>
                      {it.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {/* Attendance + Leave */}
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          {/* Attendance */}
          <div className="bg-[#0B132B]/50 border border-white/10 p-6">
            <div className="flex items-center gap-2 overline"><Clock size={12} /> Today · Attendance</div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <div className="text-slate-500 text-xs font-mono">
                  {new Date().toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                </div>
                <div className="mt-2 flex gap-4">
                  <div>
                    <div className="overline mb-1">Check-in</div>
                    <div className="text-white font-mono text-lg">{fmtTime(todayEntry?.check_in)}</div>
                  </div>
                  <div>
                    <div className="overline mb-1">Check-out</div>
                    <div className="text-white font-mono text-lg">{fmtTime(todayEntry?.check_out)}</div>
                  </div>
                  <div>
                    <div className="overline mb-1">Hours</div>
                    <div className="text-white font-mono text-lg">
                      {todayEntry?.hours != null ? `${todayEntry.hours}h` : "—"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={checkIn} disabled={!!todayEntry?.check_in || busy === "in"}
                        data-testid="hrms-check-in"
                        className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium px-3 py-2 rounded-sm">
                  {busy === "in" ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                  Check in
                </button>
                <button onClick={checkOut} disabled={!todayEntry?.check_in || !!todayEntry?.check_out || busy === "out"}
                        data-testid="hrms-check-out"
                        className="inline-flex items-center gap-2 border border-white/15 hover:border-white disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium px-3 py-2 rounded-sm">
                  {busy === "out" ? <Loader2 size={12} className="animate-spin" /> : <StopCircle size={12} />}
                  Check out
                </button>
              </div>
            </div>
            <div className="mt-6">
              <div className="overline mb-2">This month</div>
              <div className="grid grid-cols-6 md:grid-cols-10 gap-1">
                {attendance.slice(0, 30).reverse().map((a) => (
                  <div key={a.date}
                       title={`${a.date} · ${a.hours ?? 0}h`}
                       className={`text-[9px] font-mono px-1.5 py-1 text-center border ${
                         a.hours ? "bg-green-500/10 border-green-500/40 text-green-300" :
                                   "bg-white/5 border-white/10 text-slate-400"
                       }`}>
                    {a.date.slice(-2)}
                  </div>
                ))}
                {attendance.length === 0 && (
                  <div className="col-span-full text-slate-500 text-xs">No attendance yet.</div>
                )}
              </div>
            </div>
          </div>

          {/* Leave */}
          <div className="bg-[#0B132B]/50 border border-white/10 p-6">
            <div className="flex items-center gap-2 overline"><CalIcon size={12} /> Leave requests</div>
            <form onSubmit={submitLeave} data-testid="hrms-leave-form"
                  className="mt-4 grid grid-cols-2 gap-2">
              <select value={leaveForm.type}
                      onChange={(e) => setLeaveForm({ ...leaveForm, type: e.target.value })}
                      data-testid="hrms-leave-type"
                      className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500">
                <option value="casual">Casual</option>
                <option value="sick">Sick</option>
                <option value="earned">Earned</option>
                <option value="unpaid">Unpaid</option>
              </select>
              <input type="date" required value={leaveForm.from_date}
                     onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })}
                     data-testid="hrms-leave-from"
                     className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
              <input type="date" required value={leaveForm.to_date}
                     onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })}
                     data-testid="hrms-leave-to"
                     className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
              <input required placeholder="Reason" value={leaveForm.reason}
                     onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                     data-testid="hrms-leave-reason"
                     className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
              <button type="submit" disabled={busy === "leave"}
                      data-testid="hrms-leave-submit"
                      className="col-span-2 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm px-4 py-2.5 rounded-sm">
                {busy === "leave" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Submit leave request
              </button>
            </form>
            <div className="mt-5">
              <div className="overline mb-2">Recent requests</div>
              <ul className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {leaves.map((l) => (
                  <li key={l.id} data-testid={`hrms-leave-item-${l.id}`}
                      className="flex items-center justify-between bg-[#040812]/60 border border-white/10 px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-white text-sm">
                        {l.type} · {l.from_date} → {l.to_date}
                      </div>
                      <div className="text-slate-500 text-[10px] font-mono uppercase tracking-widest">
                        {l.days} day(s) · {l.reason}
                      </div>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${
                      l.status === "approved" ? "bg-green-500/15 text-green-300 border-green-500/40" :
                      l.status === "rejected" ? "bg-red-500/15 text-red-300 border-red-500/40" :
                                                "bg-yellow-500/15 text-yellow-300 border-yellow-500/40"
                    }`}>{l.status}</span>
                  </li>
                ))}
                {leaves.length === 0 && (
                  <li className="text-slate-500 text-xs">No leave requests yet.</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Payslips */}
        <div className="mt-6 bg-[#0B132B]/50 border border-white/10 p-6">
          <div className="flex items-center gap-2 overline">
            <FileDown size={12} /> Payslips
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-2">
            {payslips.map((ps) => (
              <div key={ps.id} data-testid={`hrms-payslip-${ps.id}`}
                   className="flex items-center justify-between bg-[#040812]/60 border border-white/10 px-3 py-2.5">
                <div>
                  <div className="text-white text-sm">{ps.month}</div>
                  <div className="text-slate-500 text-[10px] font-mono uppercase tracking-widest">
                    Net {fmtINR(ps.net_pay)} · {ps.payslip_no}
                  </div>
                </div>
                <button onClick={() => downloadPayslip(ps)}
                        data-testid={`hrms-payslip-download-${ps.id}`}
                        className="text-slate-400 hover:text-white">
                  <FileDown size={14} />
                </button>
              </div>
            ))}
            {payslips.length === 0 && (
              <div className="md:col-span-3 text-slate-500 text-xs">
                No payslips generated yet. Your first payslip will appear here after month-end payroll.
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="mt-8 flex items-center justify-center text-slate-500 text-xs">
            <Loader2 size={16} className="animate-spin" />
          </div>
        )}
      </div>
    </main>
  );
}
